import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "~/api/client";
import { Customer, Invoice, InvoiceStatus, Service, customerName, formatEur } from "~/domain/types";
import { dateCompare, naturalCompare } from "~/domain/sorting";

function invoiceCustomerName(invoice: Invoice) {
  try {
    const customer = JSON.parse(invoice.patient_snapshot) as {
      company_name?: string | null;
      first_name?: string | null;
      last_name?: string | null;
    };
    return (
      customer.company_name ||
      `${customer.first_name ?? ""} ${customer.last_name ?? ""}`.trim()
    );
  } catch {
    return "";
  }
}

export function InvoicesPage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [search,setSearch]=useState("");
  const [sort,setSort]=useState("date-desc");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);

  const { data: result, isLoading } = useQuery({
    queryKey: ["invoices", statusFilter],
    queryFn: () =>
      api.get<{ invoices: Invoice[] }>(
        `/invoices?status=${statusFilter}&limit=100&offset=0`,
      ),
  });

  const handleDownloadPDF = async (invoiceId: string) => {
    try {
        const blob = await api.download(`/invoices/${invoiceId}/pdf`);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `invoice-${invoiceId}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF Download fehlgeschlagen:", err);
    }
  };
  const displayedInvoices=(result?.invoices??[])
    .filter(invoice=>`${invoice.invoice_number} ${invoiceCustomerName(invoice)} ${invoice.status}`.toLocaleLowerCase("de").includes(search.toLocaleLowerCase("de")))
    .sort((left,right)=>{
      if(sort==="date-asc")return dateCompare(left.invoice_date,right.invoice_date);
      if(sort==="number-asc")return naturalCompare(left.invoice_number,right.invoice_number);
      if(sort==="number-desc")return naturalCompare(right.invoice_number,left.invoice_number);
      if(sort==="amount-asc")return left.total_cent-right.total_cent;
      if(sort==="amount-desc")return right.total_cent-left.total_cent;
      return dateCompare(right.invoice_date,left.invoice_date);
    });

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Rechnungen</h1>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          + Neue Rechnung
        </button>
      </div>

      <div className="mb-4 flex flex-wrap gap-3">
        <input value={search} onChange={event=>setSearch(event.target.value)} placeholder="Nummer, Kunde oder Status suchen…" className="min-w-64 flex-1 rounded-md border px-3 py-2"/>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-md border border-gray-300 px-4 py-2 text-gray-900 focus:border-blue-500 focus:outline-none"
        >
          <option value="all">Alle Status</option>
          <option value="entwurf">Entwurf</option>
          <option value="finalisiert">Finalisiert</option>
          <option value="versendet">Versendet</option>
          <option value="teilbezahlt">Teilbezahlt</option>
          <option value="bezahlt">Bezahlt</option>
          <option value="ueberfaellig">Überfällig</option>
          <option value="storniert">Storniert</option>
          <option value="gemahnt">Gemahnt</option>
          <option value="abgeschrieben">Abgeschrieben</option>
        </select>
        <select value={sort} onChange={event=>setSort(event.target.value)} className="rounded-md border px-3 py-2"><option value="date-desc">Datum: neueste zuerst</option><option value="date-asc">Datum: älteste zuerst</option><option value="number-asc">Nummer aufsteigend</option><option value="number-desc">Nummer absteigend</option><option value="amount-desc">Betrag absteigend</option><option value="amount-asc">Betrag aufsteigend</option></select>
      </div>

      {isLoading ? (
        <div className="text-center text-gray-500">Laden...</div>
      ) : displayedInvoices.length === 0 ? (
        <div className="text-center text-gray-500">
          Keine Rechnungen gefunden
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full">
            <thead className="border-b border-gray-200 bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                  Rechnungsnummer
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                  Datum
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                  Kunde
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                  Betrag
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-gray-900">
                  Aktionen
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {displayedInvoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {invoice.invoice_number}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {new Date(invoice.invoice_date).toLocaleDateString("de-DE")}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {invoiceCustomerName(invoice) || "–"}
                  </td>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                    {formatEur(invoice.total_cent)}
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={invoice.status} />
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => setSelectedInvoiceId(invoice.id)}
                      className="text-sm font-medium text-blue-600 hover:text-blue-700"
                    >
                      Details & bearbeiten
                    </button>
                    {invoice.status !== "entwurf" && (
                      <button
                        onClick={() => handleDownloadPDF(invoice.id)}
                        className="ml-4 text-sm font-medium text-blue-600 hover:text-blue-700"
                      >
                        PDF herunterladen
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isCreateModalOpen && (
        <CreateInvoiceModal
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["invoices"] });
            setIsCreateModalOpen(false);
          }}
        />
      )}
      {selectedInvoiceId && (
        <InvoiceDetailModal
          invoiceId={selectedInvoiceId}
          onClose={() => setSelectedInvoiceId(null)}
          onChanged={() => {
            queryClient.invalidateQueries({ queryKey: ["invoices"] });
            queryClient.invalidateQueries({ queryKey: ["tax-summary"] });
          }}
          onDownload={handleDownloadPDF}
        />
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    entwurf: "bg-gray-100 text-gray-800",
    finalisiert: "bg-blue-100 text-blue-800",
    bezahlt: "bg-green-100 text-green-800",
    ueberfaellig: "bg-red-100 text-red-800",
    storniert: "bg-yellow-100 text-yellow-800",
  };
  return (
    <span
      className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${colors[status] || "bg-gray-100 text-gray-800"}`}
    >
      {status}
    </span>
  );
}

type InvoiceItem = {
  id: string;
  performed_at: string;
  item_number: string;
  item_name: string;
  factor: number;
  quantity: number;
  unit_price_cent: number;
  total_cent: number;
};
type Reminder = {
  id: string;
  level: number;
  reminded_at: string;
  fee_cent: number;
};
type InvoiceDetail = Invoice & {
  items: InvoiceItem[];
  open_services: Service[];
  reminders: Reminder[];
  payments: Array<{ id:string; paid_at:string; amount_cent:number }>;
};

function InvoiceDetailModal({
  invoiceId,
  onClose,
  onChanged,
  onDownload,
}: {
  invoiceId: string;
  onClose: () => void;
  onChanged: () => void;
  onDownload: (id: string) => Promise<void>;
}) {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["invoice", invoiceId],
    queryFn: () => api.get<InvoiceDetail>(`/invoices/${invoiceId}`),
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["invoice", invoiceId] });
    onChanged();
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
    <div className="max-h-[94vh] w-full max-w-5xl overflow-auto rounded-xl bg-white p-6 shadow-xl">
      {isLoading && <p>Laden…</p>}
      {error && <p className="text-red-600">{error.message}</p>}
      {data && <InvoiceEditor key={`${data.updated_at}-${data.status}`} detail={data} refresh={refresh} onClose={onClose} onDownload={onDownload}/>}
    </div>
  </div>;
}

function InvoiceEditor({
  detail,
  refresh,
  onClose,
  onDownload,
}: {
  detail: InvoiceDetail;
  refresh: () => void;
  onClose: () => void;
  onDownload: (id: string) => Promise<void>;
}) {
  const [items,setItems]=useState(detail.items.map(item=>({...item})));
  const [dueDate,setDueDate]=useState(detail.due_date);
  const [addIds,setAddIds]=useState<string[]>([]);
  const [status,setStatus]=useState<InvoiceStatus>(detail.status);
  const [reminderLevel,setReminderLevel]=useState(0);
  const [reminderFee,setReminderFee]=useState(0);
  const [paymentAmount,setPaymentAmount]=useState("");
  const [paymentDate,setPaymentDate]=useState(new Date().toISOString().slice(0,10));
  const [message,setMessage]=useState("");
  const [finalizing,setFinalizing]=useState(false);
  const [previewing,setPreviewing]=useState(false);
  const draft=detail.status==="entwurf";
  const mutate=useMutation({
    mutationFn:({path,body}:{path:string;body?:unknown})=>body===undefined?api.post(path,{}):api.put(path,body),
    onSuccess:()=>{setMessage("Gespeichert.");refresh()},
    onError:(err)=>setMessage(err instanceof Error?err.message:"Aktion fehlgeschlagen."),
  });
  const saveItems=()=>mutate.mutate({path:`/invoices/${detail.id}/items`,body:{due_date:dueDate,items,add_service_ids:addIds}});
  const saveAndFinalize=async()=>{
    setFinalizing(true);setMessage("");
    try{
      await api.put(`/invoices/${detail.id}/items`,{due_date:dueDate,items,add_service_ids:addIds});
      await api.put(`/invoices/${detail.id}/finalize`,{});
      setMessage("Rechnung wurde finalisiert und als PDF gespeichert.");
      refresh();
    }catch(error){setMessage(error instanceof Error?error.message:"Finalisierung fehlgeschlagen.")}
    finally{setFinalizing(false)}
  };
  const saveAndPreview=async()=>{
    setPreviewing(true);setMessage("");
    try{
      await api.put(`/invoices/${detail.id}/items`,{due_date:dueDate,items,add_service_ids:addIds});
      await api.put(`/invoices/${detail.id}/refresh-company`,{});
      refresh();
      await onDownload(detail.id);
      setMessage("PDF-Vorschau wurde aus dem aktuellen Entwurf erzeugt.");
    }catch(error){setMessage(error instanceof Error?error.message:"PDF-Vorschau fehlgeschlagen.")}
    finally{setPreviewing(false)}
  };
  const createReminder=useMutation({
    mutationFn:()=>api.post<{id:string}>(`/invoices/${detail.id}/reminders`,{level:reminderLevel,fee_cent:Math.round(reminderFee*100)}),
    onSuccess:()=>{setMessage("Dokument wurde erstellt.");refresh()},
    onError:(err)=>setMessage(err instanceof Error?err.message:"Dokument konnte nicht erstellt werden."),
  });
  const recordPayment=useMutation({
    mutationFn:()=>api.post(`/invoices/${detail.id}/payments`,{
      amount_cent:Math.round(Number(paymentAmount)*100),
      paid_at:paymentDate,
      method:"Überweisung",
    }),
    onSuccess:()=>{setMessage("Zahlung wurde erfasst.");setPaymentAmount("");refresh()},
    onError:(err)=>setMessage(err instanceof Error?err.message:"Zahlung konnte nicht erfasst werden."),
  });
  const updateItem=(id:string,key:keyof InvoiceItem,value:string|number)=>setItems(items.map(item=>item.id===id?{...item,[key]:value}:item));
  return <div>
    <div className="mb-5 flex items-start justify-between gap-4"><div><h2 className="text-2xl font-bold">Rechnung {detail.invoice_number}</h2><div className="mt-1 flex items-center gap-3"><StatusBadge status={detail.status}/><span className="font-bold">{formatEur(detail.total_cent)}</span></div></div><button onClick={onClose} className="rounded-md border px-3 py-2">Schließen</button></div>
    {message&&<div className={`mb-4 rounded-md p-3 text-sm ${message.includes("Gespeichert")||message.includes("erstellt")?"bg-green-50 text-green-700":"bg-red-50 text-red-700"}`}>{message}</div>}
    {draft ? <section className="space-y-4">
      <div className="flex flex-wrap items-end gap-3"><label className="text-sm font-medium">Fällig am<input type="date" value={dueDate} onChange={e=>setDueDate(e.target.value)} className="mt-1 block rounded-md border px-3 py-2"/></label><button onClick={()=>mutate.mutate({path:`/invoices/${detail.id}/refresh-company`,body:{}})} className="rounded-md border border-blue-300 px-3 py-2 text-blue-700">Aktuelle Firmendaten übernehmen</button><button disabled={previewing} onClick={saveAndPreview} className="rounded-md border px-3 py-2 disabled:opacity-50">{previewing?"Erzeuge Vorschau…":"Aktuellen Entwurf als PDF ansehen"}</button></div>
      <div className="overflow-x-auto rounded-lg border"><table className="min-w-full text-sm"><thead className="bg-gray-50"><tr>{["Datum","Nr.","Bezeichnung","Faktor","Menge","Einzelpreis","Betrag",""].map(x=><th key={x} className="px-3 py-2 text-left">{x}</th>)}</tr></thead><tbody className="divide-y">{items.map(item=><tr key={item.id}>
        <td className="px-2 py-2"><input type="date" value={item.performed_at.slice(0,10)} onChange={e=>updateItem(item.id,"performed_at",e.target.value)} className="w-32 rounded border px-2 py-1"/></td>
        <td className="px-2 py-2"><input value={item.item_number} onChange={e=>updateItem(item.id,"item_number",e.target.value)} className="w-20 rounded border px-2 py-1"/></td>
        <td className="px-2 py-2"><input value={item.item_name} onChange={e=>updateItem(item.id,"item_name",e.target.value)} className="min-w-48 rounded border px-2 py-1"/></td>
        <td className="px-2 py-2"><input type="number" min=".01" step=".1" value={item.factor} onChange={e=>updateItem(item.id,"factor",Number(e.target.value))} className="w-20 rounded border px-2 py-1"/></td>
        <td className="px-2 py-2"><input type="number" min="1" value={item.quantity} onChange={e=>updateItem(item.id,"quantity",Number(e.target.value))} className="w-16 rounded border px-2 py-1"/></td>
        <td className="px-2 py-2"><input type="number" min="0" step=".01" value={item.unit_price_cent/100} onChange={e=>updateItem(item.id,"unit_price_cent",Math.round(Number(e.target.value)*100))} className="w-24 rounded border px-2 py-1"/></td>
        <td className="px-3 py-2">{formatEur(Math.round(item.factor*item.quantity*item.unit_price_cent))}</td><td className="px-3 py-2"><button onClick={()=>setItems(items.filter(current=>current.id!==item.id))} className="text-red-600">Entfernen</button></td>
      </tr>)}</tbody></table></div>
      {detail.open_services.length>0&&<fieldset className="rounded-lg border p-4"><legend className="font-bold">Weitere offene Leistungen hinzufügen</legend>{detail.open_services.map(service=><label key={service.id} className="mt-2 flex gap-2"><input type="checkbox" checked={addIds.includes(service.id)} onChange={e=>setAddIds(e.target.checked?[...addIds,service.id]:addIds.filter(id=>id!==service.id))}/>{new Date(service.performed_at).toLocaleDateString("de-DE")} · {service.item_number} · {service.item_name} · {formatEur(service.total_cent)}</label>)}</fieldset>}
      <div className="flex flex-wrap justify-end gap-3"><button onClick={saveItems} className="rounded-md bg-blue-600 px-4 py-2 text-white">Entwurf speichern</button><button disabled={finalizing} onClick={saveAndFinalize} className="rounded-md bg-green-600 px-4 py-2 text-white disabled:opacity-50">{finalizing?"Finalisiere…":"Speichern, finalisieren & PDF erzeugen"}</button></div>
    </section> : <section className="space-y-6">
      <div className="flex flex-wrap items-end gap-3"><button onClick={()=>onDownload(detail.id)} className="rounded-md bg-blue-600 px-4 py-2 text-white">PDF herunterladen</button><button onClick={()=>mutate.mutate({path:`/invoices/${detail.id}/reopen`,body:{}})} className="rounded-md border border-amber-400 px-4 py-2 text-amber-800">Wieder als Entwurf öffnen</button><label className="text-sm font-medium">Status<select value={status} onChange={e=>setStatus(e.target.value as InvoiceStatus)} className="ml-2 rounded-md border px-3 py-2">{["finalisiert","versendet","teilbezahlt","bezahlt","ueberfaellig","gemahnt","storniert","abgeschrieben"].map(value=><option key={value}>{value}</option>)}</select></label><button onClick={()=>mutate.mutate({path:`/invoices/${detail.id}/status`,body:{status}})} className="rounded-md border px-3 py-2">Status speichern</button></div>
      <div className="rounded-lg border p-4"><h3 className="mb-3 text-lg font-bold">Zahlungen</h3><div className="flex flex-wrap items-end gap-3"><label className="text-sm">Betrag in Euro<input type="number" min=".01" step=".01" value={paymentAmount} onChange={e=>setPaymentAmount(e.target.value)} className="mt-1 block rounded border px-3 py-2"/></label><label className="text-sm">Zahlungsdatum<input type="date" value={paymentDate} onChange={e=>setPaymentDate(e.target.value)} className="mt-1 block rounded border px-3 py-2"/></label><button disabled={!paymentAmount||recordPayment.isPending} onClick={()=>recordPayment.mutate()} className="rounded-md bg-green-600 px-4 py-2 text-white disabled:opacity-50">Zahlung erfassen</button><div className="ml-auto text-right"><div className="text-sm text-gray-500">Noch offen</div><strong>{formatEur(Math.max(0,detail.total_cent-detail.payments.reduce((sum,p)=>sum+p.amount_cent,0)))}</strong></div></div>{detail.payments.length>0&&<div className="mt-3 space-y-1 text-sm">{detail.payments.map(payment=><div key={payment.id}>{new Date(payment.paid_at).toLocaleDateString("de-DE")} · {formatEur(payment.amount_cent)}</div>)}</div>}</div>
      <div className="rounded-lg border p-4"><h3 className="mb-3 text-lg font-bold">Zahlungserinnerung / Mahnung</h3><div className="flex flex-wrap items-end gap-3"><label className="text-sm">Dokument<select value={reminderLevel} onChange={e=>setReminderLevel(Number(e.target.value))} className="mt-1 block rounded border px-3 py-2"><option value={0}>Zahlungserinnerung</option><option value={1}>1. Mahnung</option><option value={2}>2. Mahnung</option><option value={3}>3. Mahnung</option></select></label><label className="text-sm">Gebühr in Euro<input type="number" min="0" step=".01" value={reminderFee} onChange={e=>setReminderFee(Number(e.target.value))} className="mt-1 block rounded border px-3 py-2"/></label><button onClick={()=>createReminder.mutate()} className="rounded-md bg-amber-600 px-4 py-2 text-white">Dokument erzeugen</button></div>
      {detail.reminders.length>0&&<div className="mt-4 space-y-2">{detail.reminders.map(reminder=><div key={reminder.id} className="flex items-center justify-between rounded bg-gray-50 p-3"><span>{reminder.level===0?"Zahlungserinnerung":`${reminder.level}. Mahnung`} vom {new Date(reminder.reminded_at).toLocaleDateString("de-DE")} · Gebühr {formatEur(reminder.fee_cent)}</span><button onClick={async()=>{const blob=await api.download(`/invoices/${detail.id}/reminder-pdf?id=${reminder.id}`);const url=URL.createObjectURL(blob);const a=document.createElement("a");a.href=url;a.download=`${reminder.level===0?"erinnerung":"mahnung"}-${detail.invoice_number}.pdf`;a.click();URL.revokeObjectURL(url)}} className="text-blue-600">PDF herunterladen</button></div>)}</div>}</div>
    </section>}
  </div>;
}

function CreateInvoiceModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [patientId, setPatientId] = useState("");
  const [dueDate, setDueDate] = useState("");

  const { data: patients } = useQuery({
    queryKey: ["patients"],
    queryFn: () => api.get<{ patients: Customer[] }>("/patients?limit=100&offset=0"),
  });
  const { data: detail } = useQuery({
    queryKey: ["customer", patientId],
    queryFn: () => api.get<{ services: Service[] }>(`/patients/${patientId}`),
    enabled: Boolean(patientId),
  });
  const openServices = detail?.services.filter((service) => !service.invoice_id) ?? [];

  const createMutation = useMutation({
    mutationFn: (data: { customer_id: string; due_date?: string }) =>
      api.post("/invoices", data),
    onSuccess,
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
        <h2 className="mb-4 text-xl font-bold text-gray-900">Neue Rechnung</h2>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate({
              customer_id: patientId,
              ...(dueDate ? { due_date: dueDate } : {}),
            });
          }}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Kunde *
            </label>
            <select
              required
              value={patientId}
              onChange={(e) => setPatientId(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 focus:border-blue-500 focus:outline-none"
            >
              <option value="">– Wählen –</option>
              {patients?.patients?.map((p) => (
                <option key={p.id} value={p.id}>
                  {customerName(p)}
                </option>
              ))}
            </select>
          </div>
          {patientId && (
            <div className="rounded-md bg-blue-50 p-3 text-sm">
              <strong>
                {openServices.length} offene Position
                {openServices.length === 1 ? "" : "en"}
              </strong>
              <span className="float-right font-bold">
                {formatEur(openServices.reduce((sum, s) => sum + s.total_cent, 0))}
              </span>
              <p className="mt-1 text-blue-800">
                Alle offenen Leistungen werden mit ihrem Leistungsdatum übernommen.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Fälligkeitsdatum
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 focus:border-blue-500 focus:outline-none"
            />
          </div>

          <div className="flex gap-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="flex-1 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              Erstellen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
